/**
 * The offered typefaces, as complete literal CSS stacks.
 *
 * Every member is OFL-licensed, so the later "self-host the woff2" follow-up is
 * licence-clean and purely additive. `System` is the default and is the ONE
 * member that will never gain a webfont — which is what freezes an
 * already-published form's rendering forever.
 *
 * Today `display === body` for every member: a short closed list of single
 * families is what an author picking "Merriweather" expects (Merriweather
 * headings AND body). The pair shape is the seam that lets design introduce a
 * display pairing later as a table-row edit rather than a type change.
 *
 * Deliberately EXCLUDED, and why (both are traps, not oversights):
 *  - `Inter` — the oportal shell loads Inter from fonts.googleapis.com in its
 *    own index.html, so an Inter option would render the real face inside the
 *    admin preview while the donor page (same-origin CSP, no font-src) fell back
 *    to the system sans. That is the one way the picker could lie about what the
 *    donor sees.
 *  - `OpenSans` — its natural fallback chain is Segoe UI / Verdana, i.e. exactly
 *    what `System` already resolves to on Windows. An axis member that is
 *    indistinguishable from the default reads as a broken setting.
 */
export const DF_FONT_STACKS = {
    System: {
        body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        display: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    Poppins: {
        body: '"Poppins", "Century Gothic", Futura, "Trebuchet MS", sans-serif',
        display: '"Poppins", "Century Gothic", Futura, "Trebuchet MS", sans-serif',
    },
    Montserrat: {
        body: '"Montserrat", "Gill Sans MT", "Gill Sans", Verdana, sans-serif',
        display: '"Montserrat", "Gill Sans MT", "Gill Sans", Verdana, sans-serif',
    },
    Lato: {
        body: '"Lato", Tahoma, "DejaVu Sans", sans-serif',
        display: '"Lato", Tahoma, "DejaVu Sans", sans-serif',
    },
    Merriweather: {
        body: '"Merriweather", Georgia, "Times New Roman", serif',
        display: '"Merriweather", Georgia, "Times New Roman", serif',
    },
};
/**
 * The corner-rounding ladder: one authored step -> all seven radius tokens.
 *
 * Governing rule — **rounding applies to rectangular surfaces; pill-ness is a
 * design signature, not a maximum; circles are shapes, not corners.** So the
 * `border-radius: 50%` discs (the impact dot, the progress circles, the
 * thank-you check badge) and the 2px progress-connector cap are NOT scale
 * participants and stay put at every step, as does DonationForm.vue's
 * `border-radius: 0` override on the `--fill` card (a full-bleed embed has no
 * outer corner to round).
 *
 * `Medium` reproduces the previously-baked "Modern" preset value for value, so
 * every form published before this axis existed renders pixel-identically.
 * `None` is 0 on all seven INCLUDING the two pills: leaving a 999px language tag
 * next to square buttons is precisely the broken look the axis exists to fix.
 * `preset`/`tag` also de-pill at `Slight` — an author choosing the lightest step
 * means "less round than Medium", and a perfect pill there reads as "my setting
 * did not apply". Blocks never become pills (`box` caps at 18px, `card` at
 * 28px); only `btn` goes stadium, at `Rounded`.
 *
 * Invariants the spec pins: every token monotonically non-decreasing across the
 * four steps; `seg < box` whenever `box > 0` (the segmented thumb nests inside
 * its track); every token `0px` at `None`; the `Medium` column equals the seven
 * literals still declared as static defaults in tokens.css.
 */
export const DF_RADIUS_SCALE = {
    None: {
        "--df-radius-card": "0px",
        "--df-radius-btn": "0px",
        "--df-radius-field": "0px",
        "--df-radius-preset": "0px",
        "--df-radius-tag": "0px",
        "--df-radius-box": "0px",
        "--df-radius-seg": "0px",
    },
    Slight: {
        "--df-radius-card": "10px",
        "--df-radius-btn": "6px",
        "--df-radius-field": "6px",
        "--df-radius-preset": "10px",
        "--df-radius-tag": "10px",
        "--df-radius-box": "6px",
        "--df-radius-seg": "4px",
    },
    Medium: {
        "--df-radius-card": "20px",
        "--df-radius-btn": "12px",
        "--df-radius-field": "11px",
        "--df-radius-preset": "999px",
        "--df-radius-tag": "999px",
        "--df-radius-box": "12px",
        "--df-radius-seg": "9px",
    },
    Rounded: {
        "--df-radius-card": "28px",
        "--df-radius-btn": "999px",
        // Capped, NOT pilled: BaseInput is ~49px tall with 13px/15px padding, so a
        // 999px radius clamps to ~24.5px a side and the first glyph would sit inside
        // the curve.
        "--df-radius-field": "16px",
        "--df-radius-preset": "999px",
        "--df-radius-tag": "999px",
        "--df-radius-box": "18px",
        "--df-radius-seg": "12px",
    },
};
const FONT_FAMILIES = Object.keys(DF_FONT_STACKS);
const BORDER_RADII = Object.keys(DF_RADIUS_SCALE);
/**
 * Total: resolves the document's nullable font token to a catalogue member.
 * Unspecified (a form that never picked one, or a branding frozen before the
 * field existed) and anything unrecognised both fall back to 'System'.
 */
export function toFontFamily(value) {
    return FONT_FAMILIES.includes(value) ? value : "System";
}
/**
 * Total: resolves the document's nullable rounding token to a ladder step.
 * Unspecified and unrecognised both fall back to 'Medium' — the step that
 * reproduces the shipped scale, so a partial or stale document renders exactly
 * as it did before the axis existed.
 */
export function toBorderRadius(value) {
    return BORDER_RADII.includes(value) ? value : "Medium";
}
/**
 * Derive the 9 shape custom properties from the document branding. Never throws:
 * a null / unknown / absent token resolves to 'System' + 'Medium'.
 *
 * `--df-font-display` is emitted as a COMPLETE literal stack rather than
 * `"<Display>", var(--df-font-body)`: the indirection only made sense while the
 * two were baked static defaults, and resolving one authored token through
 * another custom property makes the emitted value unassertable.
 */
export function brandingToShapeVars(branding) {
    const stack = DF_FONT_STACKS[toFontFamily(branding.fontFamily)];
    return {
        "--df-font-body": stack.body,
        "--df-font-display": stack.display,
        ...DF_RADIUS_SCALE[toBorderRadius(branding.borderRadius)],
    };
}
